// ✅ src/pages/VehicleV2/BatteryCardReport_AllPrint.jsx
// This page:
// 1) reads filters from navigate("/vehicle/BatteryCardReport_AllPrint", { state: { filters } })
// 2) calls GET /api/batteryCardReport/list with those filters
// 3) renders the same print table with real API data
//
// Backend expected:
//   app.use("/api/batteryCardReport", require("./routes/batteryCardReport"));
//
// List endpoint params expected by backend we built:
//   branchType, branch, fromDate, toDate,
//   batteryCode, batteryType, serialNo,
//   vehicleCode, vehicleName, vehicleRegNo,
//   batteryStatus, batteryMake, supplier,
//   vehicleMainType, status
//
// If your filter object keys are different, adjust mapFiltersToParams() below.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/batteryCardReport";

const toStr = (v) => String(v ?? "").trim();
const isEmpty = (v) => !toStr(v);

function mapFiltersToParams(filters) {
  // ✅ Adapt your listing page filter model into backend query params.
  // Your listing page earlier had:
  // { fromDate,toDate,branch,vehicleCode,batteryCode,batteryType,batteryMake,vehicleMainType }
  // Add any extra keys you use.
  return {
    branchType: toStr(filters.branchType || "HO"),
    branch: toStr(filters.branch || "Select"),

    fromDate: toStr(filters.fromDate || ""),
    toDate: toStr(filters.toDate || ""),

    vehicleCode: toStr(filters.vehicleCode || ""),
    vehicleName: toStr(filters.vehicleName || ""), // optional if you have it
    vehicleRegNo: toStr(filters.vehicleRegNo || filters.vehicleRegno || ""),

    batteryCode: toStr(filters.batteryCode || "Select"),
    batteryType: toStr(filters.batteryType || "Select"),
    serialNo: toStr(filters.serialNo || "Select"),

    batteryStatus: toStr(filters.batteryStatus || "Select"),
    batteryMake: toStr(filters.batteryMake || ""),

    supplier: toStr(filters.supplier || "Select"),

    // vehicleMainType should be dropdown value (vehicletypeindex) or Select
    vehicleMainType: toStr(filters.vehicleMainType || "Select"),

    // ddl_status equivalent
    status: toStr(filters.status || "All"),
  };
}

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const BatteryCardMasterReport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Filters passed from listing page
  const filters = useMemo(() => location?.state?.filters || {}, [location]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [batteryData, setBatteryData] = useState([]);

  const params = useMemo(() => mapFiltersToParams(filters), [filters]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/list`, { params });
        const rows = res?.data?.rows || [];

        // Normalize keys used by table
        const normalized = rows.map((r, i) => ({
          sno: i + 1,
          vehicleMainType: r.jobMainType ?? r.vehicleMainType ?? "",
          vehicleCode: r.vehicleCode ?? "",
          vehicleName: r.vehicleName ?? "",
          vehicleRegno: r.vehicleRegNo ?? r.vehicleRegno ?? "",
          batteryCode: r.batteryCode ?? "",
          batterySerialNo: r.batterySerialNumber ?? r.batterySerialNo ?? "",
          batteryType: r.batteryType ?? "",
          batteryMake: r.batteryMake ?? "",
          issuedBranch: r.issuedBranch ?? "",
          currentBranch: r.currentBranch ?? r.carriedBranch ?? "",
          purchasedAmt: fmtMoney(r.purchaseAmount ?? r.purchasedAmt ?? r.fld_purchaseamount),
          issuedDate: r.issueDate ?? r.issuedDate ?? "",
          expectedLife: r.expectedLife ?? "",
          warrantyPeriod: r.warrantyPeriod ?? "",
          supplier: r.supplier ?? "",
          purposeOfPurchase: r.purposeOfPurchase ?? r.purchasedFrom ?? r.purpose ?? "",
        }));

        if (alive) setBatteryData(normalized);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 1px !important; }
          .print-landscape { 
            size: A4 landscape; 
            margin: 0.3in;
          }
        }
        @page {
          size: A4 landscape;
          margin: 0.3in;
        }
      `}</style>

      <div className="p-2 print:p-1 print-landscape">
        {/* Top actions (not printed) */}
        <div className="no-print flex items-center justify-between mb-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            ← Back
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Header Section */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Battery Card Master Report</h2>
        </div>

        {/* Filter Summary (optional) */}
        

        {/* Loading / Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading report...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">
            {err}
          </div>
        ) : null}

        {/* Main Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "30px" }}>
                  S No
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Vehicle Main Type
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Vehicle Code
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Vehicle Name
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Vehicle Regno
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "100px" }}>
                  Battery Code
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "80px" }}>
                  Battery Serial No
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Battery Type
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Battery Make
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Issued Branch
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Current Branch
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Purchased Amt
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Issued Date
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Expected Life
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "70px" }}>
                  Warranty Period
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "50px" }}>
                  Supplier
                </th>
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: "120px" }}>
                  Purpose of Purchase
                </th>
              </tr>
            </thead>

            <tbody>
              {batteryData.length === 0 ? (
                <tr>
                  <td colSpan={17} className="border border-black px-2 py-2 text-center">
                    No records found for selected filters.
                  </td>
                </tr>
              ) : (
                batteryData.map((battery, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-black px-1 py-1 text-center">{battery.sno}</td>
                    <td className="border border-black px-1 py-1">{battery.vehicleMainType}</td>
                    <td className="border border-black px-1 py-1 text-center">{battery.vehicleCode}</td>
                    <td className="border border-black px-1 py-1">{battery.vehicleName}</td>
                    <td className="border border-black px-1 py-1">{battery.vehicleRegno}</td>
                    <td className="border border-black px-1 py-1">{battery.batteryCode}</td>
                    <td className="border border-black px-1 py-1">{battery.batterySerialNo}</td>
                    <td className="border border-black px-1 py-1">{battery.batteryType}</td>
                    <td className="border border-black px-1 py-1">{battery.batteryMake}</td>
                    <td className="border border-black px-1 py-1">{battery.issuedBranch}</td>
                    <td className="border border-black px-1 py-1">{battery.currentBranch}</td>
                    <td className="border border-black px-1 py-1 text-right">{battery.purchasedAmt}</td>
                    <td className="border border-black px-1 py-1 text-center">{battery.issuedDate}</td>
                    <td className="border border-black px-1 py-1 text-center">{battery.expectedLife}</td>
                    <td className="border border-black px-1 py-1 text-center">{battery.warrantyPeriod}</td>
                    <td className="border border-black px-1 py-1 text-center">{battery.supplier}</td>
                    <td className="border border-black px-1 py-1">{battery.purposeOfPurchase}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>

        {/* Print Button (bottom) */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg transition-colors duration-200"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatteryCardMasterReport;
